import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { t } from './index';
import type { Locale, MessageKey, MessageParams, Translator } from './index';

const I18nContext = createContext<Locale>('en');

export interface I18nProviderProps {
  locale: Locale;
  children: ReactNode;
}

/** Provides the active locale to the React tree. Defaults to `en` when absent. */
export function I18nProvider({ locale, children }: I18nProviderProps) {
  return <I18nContext.Provider value={locale}>{children}</I18nContext.Provider>;
}

/** The active locale from context. */
export function useLocale(): Locale {
  return useContext(I18nContext);
}

/**
 * Translator bound to the context locale:
 * `const t = useT(); t('ec2.nav.quests')`
 */
export function useT(): Translator {
  const locale = useLocale();
  const translate = useCallback(
    (key: MessageKey, params?: MessageParams) => t(locale, key, params),
    [locale],
  );
  return useMemo(() => translate, [translate]);
}
